import { Link, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useState, useEffect, useRef } from "react";

const BrandMark = () => (
    <svg viewBox="0 0 38 38" aria-hidden="true" focusable="false">
        <circle cx="19" cy="19" r="12.5" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="19" cy="19" r="6" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="19" cy="19" r="2.5" fill="currentColor" />
    </svg>
);

const getInitials = (user) => {
    const label = String(user?.name || user?.email || "Club").trim();
    const parts = label.includes("@") ? label.split("@")[0].split(/[._-]+/) : label.split(/\s+/);

    return parts
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join("") || "CC";
};

const announceNavigationState = (isOpen) => {
    window.dispatchEvent(new CustomEvent('club-navigation-toggle', {
        detail: { isOpen },
    }));
};

const TopBar = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const { toggleTheme, isLight } = useTheme();
    const [userDropDown, setUserDropDown] = useState(false);
    const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
    const location = useLocation();
    const hideComps = matchPath('/scoreboard/:type/:id', location.pathname);
    const dropdownRef = useRef(null);
    const accountTriggerRef = useRef(null);

    const toggleMobileNavigation = () => {
        const nextState = !mobileNavigationOpen;
        setUserDropDown(false);
        setMobileNavigationOpen(nextState);
        announceNavigationState(nextState);
    };

    const toggleAccountMenu = () => {
        const nextState = !userDropDown;
        if (nextState && mobileNavigationOpen) {
            setMobileNavigationOpen(false);
            announceNavigationState(false);
        }
        setUserDropDown(nextState);
    };

    const handleLogout = async () => {
        try {
            await logout();
            window.location.href = '/';
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setUserDropDown(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setUserDropDown(false);
                accountTriggerRef.current?.focus();
            }
        };

        if (userDropDown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [userDropDown]);

    useEffect(() => {
        setUserDropDown(false);
        setMobileNavigationOpen(false);
        announceNavigationState(false);
    }, [location.pathname]);

    useEffect(() => {
        const syncNavigationState = (event) => {
            setMobileNavigationOpen(Boolean(event.detail?.isOpen));
        };

        window.addEventListener('club-navigation-toggle', syncNavigationState);
        return () => window.removeEventListener('club-navigation-toggle', syncNavigationState);
    }, []);

    useEffect(() => {
        if (!mobileNavigationOpen) return undefined;

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setMobileNavigationOpen(false);
                announceNavigationState(false);
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [mobileNavigationOpen]);

    if (hideComps) return null;

    return (
        <header className="navbar navbar-expand-md d-print-none club-topbar">
            <div className="container-xl club-topbar-inner">
                <Link to="/" className="club-panel-brand" aria-label="CueClub Manager dashboard">
                    <span className="club-panel-brand-mark"><BrandMark /></span>
                    <span className="club-panel-brand-copy">
                        <span>CueClub <strong>Manager</strong></span>
                        <small>Club operations</small>
                    </span>
                </Link>

                <div className="club-topbar-actions">
                    {isAuthenticated() ? (
                        <div className="club-account" ref={dropdownRef}>
                            <button
                                type="button"
                                ref={accountTriggerRef}
                                onClick={toggleAccountMenu}
                                className="club-account-trigger"
                                aria-expanded={userDropDown}
                                aria-haspopup="menu"
                                aria-controls="club-account-menu"
                                aria-label={`${userDropDown ? 'Close' : 'Open'} account menu for ${user?.name || 'club account'}`}
                            >
                                <span className="club-account-avatar" aria-hidden="true">{getInitials(user)}</span>
                                <span className="club-account-copy d-none d-sm-flex">
                                    <small>Club account</small>
                                    <strong>{user?.name || 'Club account'}</strong>
                                </span>
                                <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 8 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>

                            <div id="club-account-menu" className={`dropdown-menu club-account-menu ${userDropDown ? 'show' : ''}`} role="menu">
                                <div className="club-account-menu-heading">
                                    <span className="club-account-menu-avatar" aria-hidden="true">{getInitials(user)}</span>
                                    <span className="club-account-menu-identity">
                                        <small>Club account</small>
                                        <strong>{user?.name || 'CueClub Manager'}</strong>
                                        <span>{user?.email || ''}</span>
                                    </span>
                                </div>

                                <Link to={`/settings/${user?.id}`} className="club-account-menu-item" role="menuitem" onClick={() => setUserDropDown(false)}>
                                    <span className="club-account-menu-icon" aria-hidden="true">
                                        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1.03-1.56 1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 0 1 0-4h.09A1.7 1.7 0 0 0 4.65 8.9a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.88.34h.01A1.7 1.7 0 0 0 10.06 3V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.88v.01A1.7 1.7 0 0 0 21 10.06h.09a2 2 0 0 1 0 4H21A1.7 1.7 0 0 0 19.4 15Z" /></svg>
                                    </span>
                                    <span><strong>Settings</strong><small>Club, pricing and screens</small></span>
                                </Link>

                                <button type="button" className="club-account-menu-item club-theme-menu-item" onClick={toggleTheme} role="menuitem">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
                                    <span><strong>{isLight ? 'Light appearance' : 'Dark appearance'}</strong><small>Switch to {isLight ? 'dark' : 'light'} mode</small></span>
                                    <span className={`club-theme-switch ${isLight ? 'is-light' : 'is-dark'}`} aria-hidden="true"><i /></span>
                                </button>

                                <div className="club-account-menu-divider" />

                                <button type="button" className="club-account-menu-item club-logout-item" onClick={handleLogout} role="menuitem">
                                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 17l5-5-5-5M15 12H3M15 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" /></svg>
                                    <span><strong>Log out</strong><small>Return to the public site</small></span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <Link to="/login" className="club-panel-login">Log in</Link>
                    )}

                    <button
                        className={`navbar-toggler d-md-none club-menu-toggle ${mobileNavigationOpen ? 'is-open' : ''}`}
                        type="button"
                        onClick={toggleMobileNavigation}
                        aria-controls="navbar-menu"
                        aria-expanded={mobileNavigationOpen}
                        aria-label={mobileNavigationOpen ? "Close club navigation" : "Open club navigation"}
                    >
                        <span /><span /><span />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default TopBar;
